import { lazy, Suspense, useEffect, type ComponentType } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';

import { Center, MantineProvider, useComputedColorScheme } from '@mantine/core';

import { QueryClientProvider } from '@tanstack/react-query';

import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';
import '@mantine/spotlight/styles.css';

import { ClerkProvider, Show, useAuth } from '@clerk/react';

import {
  AnalyticsPropsSync,
  DefaultAppRedirect,
  OnboardingGuard,
  OreLoader,
  ProtectedRoute,
  PublicOnlyRoute,
  SentryUserSync,
} from '@/components/atoms';
import { AppErrorBoundary } from '@/components/organisms';
import { createQueryClient } from '@/lib/queryClient';
import { useSessionStore } from '@/store/sessionStore';
import {
  assayClerkTheme,
  assayClerkThemeLight,
  assayCssVariablesResolver,
  assayTheme,
} from '@/theme/assay';

/*
  Every route below is lazy so a chunk only ships when its route is visited.

  Each route must point at a page's own module (`@/pages/marketing/HomePage`),
  never a group barrel. Tree shaking does not save us here: every page behind a
  barrel is reachable, so all of them collapse into one chunk. Measured with a
  `pages/marketing` barrel in place, the eight marketing routes fused into a
  single 96 KB chunk instead of loading the ~28 KB the homepage actually needs.
  This is why `src/pages/` has no index.ts files.

  The layouts are lazy for the same reason -- it is what keeps the heavy
  app-only dependencies (the Courier SDK in AppLayout, ~184 KB) off the
  marketing path.

  `lazyNamed` exists because these modules use named exports and React.lazy
  wants a module whose default export is the component.
*/
function lazyNamed<T extends Record<string, unknown>, K extends keyof T>(
  loader: () => Promise<T>,
  name: K
) {
  return lazy(() => loader().then((m) => ({ default: m[name] as ComponentType })));
}

// Layouts
const MarketingLayout = lazyNamed(
  () => import('@/templates/MarketingLayout/MarketingLayout'),
  'MarketingLayout'
);
const AuthLayout = lazyNamed(() => import('@/templates/AuthLayout/AuthLayout'), 'AuthLayout');
const OnboardingLayout = lazyNamed(
  () => import('@/templates/OnboardingLayout/OnboardingLayout'),
  'OnboardingLayout'
);
const AppLayout = lazyNamed(() => import('@/templates/AppLayout/AppLayout'), 'AppLayout');
const MicrositeLayout = lazyNamed(
  () => import('@/templates/MicrositeLayout/MicrositeLayout'),
  'MicrositeLayout'
);

// Marketing
const HomePage = lazyNamed(() => import('@/pages/marketing/HomePage'), 'HomePage');
const PricingPage = lazyNamed(() => import('@/pages/marketing/PricingPage'), 'PricingPage');
const AboutPage = lazyNamed(() => import('@/pages/marketing/AboutPage'), 'AboutPage');
const FAQPage = lazyNamed(() => import('@/pages/marketing/FAQPage'), 'FAQPage');
const PrivacyPage = lazyNamed(() => import('@/pages/marketing/PrivacyPage'), 'PrivacyPage');
const TermsPage = lazyNamed(() => import('@/pages/marketing/TermsPage'), 'TermsPage');
const DpaPage = lazyNamed(() => import('@/pages/marketing/DpaPage'), 'DpaPage');
const NotFoundPage = lazyNamed(() => import('@/pages/marketing/NotFoundPage'), 'NotFoundPage');

// Microsites
const YouTubeAdPage = lazyNamed(() => import('@/pages/microsites/YouTubeAdPage'), 'YouTubeAdPage');

// Auth
const SignInPage = lazyNamed(() => import('@/pages/auth/SignInPage'), 'SignInPage');
const SignUpPage = lazyNamed(() => import('@/pages/auth/SignUpPage'), 'SignUpPage');

// Onboarding
const OnboardingPage = lazyNamed(
  () => import('@/pages/onboarding/OnboardingPage'),
  'OnboardingPage'
);

// App
const PipelinePage = lazyNamed(() => import('@/pages/app/PipelinePage'), 'PipelinePage');
const KeepInTouchPage = lazyNamed(() => import('@/pages/app/KeepInTouchPage'), 'KeepInTouchPage');
const ContactsPage = lazyNamed(() => import('@/pages/app/ContactsPage'), 'ContactsPage');
const ContactDetailPage = lazyNamed(
  () => import('@/pages/app/ContactDetailPage'),
  'ContactDetailPage'
);
const ContextPage = lazyNamed(() => import('@/pages/app/ContextPage'), 'ContextPage');
const AssayPage = lazyNamed(() => import('@/pages/app/AssayPage'), 'AssayPage');
const OutreachPage = lazyNamed(() => import('@/pages/app/OutreachPage'), 'OutreachPage');
const TemplatesPage = lazyNamed(() => import('@/pages/app/TemplatesPage'), 'TemplatesPage');
const RetroPage = lazyNamed(() => import('@/pages/app/RetroPage'), 'RetroPage');
const RetroHistoryPage = lazyNamed(
  () => import('@/pages/app/RetroHistoryPage'),
  'RetroHistoryPage'
);
const RetroHistoryDetailPage = lazyNamed(
  () => import('@/pages/app/RetroHistoryDetailPage'),
  'RetroHistoryDetailPage'
);
const AccountPage = lazyNamed(() => import('@/pages/app/AccountPage'), 'AccountPage');
const ImportPage = lazyNamed(() => import('@/pages/app/ImportPage'), 'ImportPage');
const VoicePage = lazyNamed(() => import('@/pages/app/VoicePage'), 'VoicePage');
const OAuthCallbackPage = lazyNamed(
  () => import('@/pages/app/OAuthCallbackPage'),
  'OAuthCallbackPage'
);
const HubSpotOAuthCallbackPage = lazyNamed(
  () => import('@/pages/app/HubSpotOAuthCallbackPage'),
  'HubSpotOAuthCallbackPage'
);
const ThemeShowcase = lazyNamed(() => import('@/pages/app/ThemeShowcase'), 'ThemeShowcase');

const queryClient = createQueryClient();
const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

if (!PUBLISHABLE_KEY) {
  throw new Error('No Clerk Publishable Key');
}

/**
 * Route-transition fallback. Deliberately OreLoader and not ScaleLoader --
 * ScaleLoader pulls the Lottie runtime (~328 KB), which would be loaded
 * eagerly here and cancel out most of the split.
 */
function RouteFallback() {
  return (
    <Center h="100vh">
      <OreLoader size="lg" />
    </Center>
  );
}

/** Clears ephemeral session state when the user signs out. */
function AuthSync() {
  const { isSignedIn } = useAuth();
  const reset = useSessionStore((s) => s.reset);

  useEffect(() => {
    if (isSignedIn === false) {
      reset();
      queryClient.clear();
    }
  }, [isSignedIn, reset]);

  return null;
}

/** Wraps ClerkProvider inside MantineProvider so it can read the resolved color scheme. */
function AppWithClerk() {
  const colorScheme = useComputedColorScheme('dark');
  const clerkAppearance = colorScheme === 'dark' ? assayClerkTheme : assayClerkThemeLight;

  return (
    <BrowserRouter>
      {/*
        Without an explicit fallback Clerk lands users on "/" after sign-in/up - the
        marketing home, which has no signed-in guard to bounce them onward. Send them
        to /app instead; DefaultAppRedirect and OnboardingGuard take it from there.
        Fallback (not force) so a deep-link `redirect_url` still wins.
      */}
      <ClerkProvider
        publishableKey={PUBLISHABLE_KEY}
        appearance={clerkAppearance}
        signInFallbackRedirectUrl="/app"
        signUpFallbackRedirectUrl="/app"
      >
        <AuthSync />
        <AnalyticsPropsSync />
        <SentryUserSync />
        <AppErrorBoundary>
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              {/* Marketing */}
              <Route element={<MarketingLayout />}>
                <Route path="/" element={<HomePage />} />
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/faq" element={<FAQPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/dpa" element={<DpaPage />} />
              </Route>

              {/* Microsites: unlinked landing pages with no marketing header/footer */}
              <Route path="/m" element={<MicrositeLayout />}>
                <Route path="ad" element={<YouTubeAdPage />} />
              </Route>

              {/* Auth - unauthenticated only */}
              <Route element={<PublicOnlyRoute />}>
                <Route element={<AuthLayout />}>
                  <Route path="/signin" element={<SignInPage />} />
                  <Route path="/signup" element={<SignUpPage />} />
                </Route>
              </Route>

              {/* Protected: must be signed in */}
              <Route element={<ProtectedRoute />}>
                {/* Onboarding */}
                <Route path="/onboarding" element={<OnboardingLayout />}>
                  <Route index element={<OnboardingPage />} />
                </Route>

                {/* App - must also have completed onboarding */}
                <Route element={<OnboardingGuard />}>
                  <Route path="/app" element={<AppLayout />}>
                    <Route index element={<DefaultAppRedirect />} />
                    <Route path="pipeline" element={<PipelinePage />} />
                    <Route path="keep-in-touch" element={<KeepInTouchPage />} />
                    <Route path="contacts" element={<ContactsPage />} />
                    <Route path="context" element={<ContextPage />} />
                    <Route path="assay" element={<AssayPage />} />
                    <Route path="outreach" element={<OutreachPage />} />
                    <Route path="templates" element={<TemplatesPage />} />
                    <Route path="retro" element={<RetroPage />} />
                    <Route path="retro/history" element={<RetroHistoryPage />} />
                    <Route path="retro/history/:id" element={<RetroHistoryDetailPage />} />
                    <Route path="account" element={<AccountPage />} />
                    <Route path="import" element={<ImportPage />} />
                    <Route path="voice" element={<VoicePage />} />
                    <Route path="contacts/:id" element={<ContactDetailPage />} />
                    <Route path="oauth/google/callback" element={<OAuthCallbackPage />} />
                    <Route path="oauth/hubspot/callback" element={<HubSpotOAuthCallbackPage />} />

                    <Route
                      path="theme"
                      element={
                        <Show when={{ feature: 'view_theme' }}>
                          <ThemeShowcase />
                        </Show>
                      }
                    />
                  </Route>
                </Route>
              </Route>

              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
        </AppErrorBoundary>
      </ClerkProvider>
    </BrowserRouter>
  );
}

function App() {
  return (
    <AppErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <MantineProvider
          theme={assayTheme}
          defaultColorScheme="auto"
          cssVariablesResolver={assayCssVariablesResolver}
        >
          <AppWithClerk />
        </MantineProvider>
      </QueryClientProvider>
    </AppErrorBoundary>
  );
}

export default App;
